/**
 * Bottom-sheet chooser for the nurse's editable profile photo.
 *
 * Image-only (Take photo · Choose from photos) plus an optional "Remove photo"
 * action when a photo is already set. Mirrors the compliance UploadSourceSheet
 * styling but drops the PDF source — an avatar is always an image.
 *
 * The parent owns open/close state and dispatches the chosen action.
 */
import { useCallback } from "react";
import { Modal, Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { Icon, Text } from "react-native-paper";

import { colors, radius, spacing, typography } from "@/lib/theme";

export type ProfilePhotoAction = "camera" | "photo" | "remove";

interface ProfilePhotoSheetProps {
    visible: boolean;
    onClose: () => void;
    onPick: (action: ProfilePhotoAction) => void;
    /** Show the "Remove photo" row only when the nurse already has one. */
    canRemove: boolean;
}

export function ProfilePhotoSheet({ visible, onClose, onPick, canRemove }: ProfilePhotoSheetProps) {
    // Close before firing so the system picker animates in cleanly (iOS drops
    // the picker presentation if it races the modal dismiss).
    const handlePick = useCallback(
        (action: ProfilePhotoAction) => {
            onClose();
            setTimeout(() => onPick(action), 50);
        },
        [onClose, onPick]
    );

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <Pressable style={styles.overlay} onPress={onClose}>
                <Pressable style={styles.sheet} onPress={(e) => e.stopPropagation()}>
                    <Text style={styles.title}>Profile photo</Text>
                    <SourceButton
                        icon="camera"
                        label="Take a photo"
                        sub="Use your camera"
                        onPress={() => handlePick("camera")}
                    />
                    <SourceButton
                        icon="image-multiple-outline"
                        label="Choose from photos"
                        sub="Pick an image from your library"
                        onPress={() => handlePick("photo")}
                    />
                    {canRemove ? (
                        <SourceButton
                            icon="trash-can-outline"
                            label="Remove photo"
                            sub="Go back to your default avatar"
                            destructive
                            onPress={() => handlePick("remove")}
                        />
                    ) : null}
                    <TouchableOpacity style={styles.cancel} onPress={onClose}>
                        <Text style={styles.cancelText}>Cancel</Text>
                    </TouchableOpacity>
                </Pressable>
            </Pressable>
        </Modal>
    );
}

function SourceButton({
    icon,
    label,
    sub,
    onPress,
    destructive
}: {
    icon: string;
    label: string;
    sub: string;
    onPress: () => void;
    destructive?: boolean;
}) {
    return (
        <TouchableOpacity style={styles.row} onPress={onPress} accessibilityRole="button">
            <View style={[styles.iconCircle, destructive && styles.iconCircleDestructive]}>
                <Icon
                    source={icon}
                    size={22}
                    color={destructive ? colors.error600 : colors.primary}
                />
            </View>
            <View style={styles.rowText}>
                <Text style={[styles.rowLabel, destructive && styles.rowLabelDestructive]}>
                    {label}
                </Text>
                <Text style={styles.rowSub}>{sub}</Text>
            </View>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.45)",
        justifyContent: "flex-end"
    },
    sheet: {
        backgroundColor: "#FFFFFF",
        borderTopLeftRadius: radius.xl,
        borderTopRightRadius: radius.xl,
        paddingTop: spacing.lg,
        paddingHorizontal: spacing.lg,
        paddingBottom: spacing.xl + 8,
        gap: spacing.sm
    },
    title: {
        ...typography.title,
        color: colors.textPrimary,
        marginBottom: spacing.sm
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        paddingVertical: spacing.md,
        paddingHorizontal: spacing.sm,
        borderRadius: radius.lg,
        backgroundColor: colors.backgroundSecondary
    },
    iconCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.primaryLight,
        alignItems: "center",
        justifyContent: "center"
    },
    iconCircleDestructive: {
        backgroundColor: colors.error50
    },
    rowText: {
        flex: 1
    },
    rowLabel: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    rowLabelDestructive: {
        color: colors.error600
    },
    rowSub: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 2
    },
    cancel: {
        marginTop: spacing.sm,
        alignItems: "center",
        paddingVertical: spacing.md
    },
    cancelText: {
        ...typography.subtitle,
        color: colors.textSecondary
    }
});
